import { useQuery } from '@apollo/client';
import React, { Dispatch, SetStateAction } from 'react';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import { R300 } from '@atlaskit/theme/colors';
import { Box, Flex, Text } from '@atlaskit/primitives';
import Badge from '@atlaskit/badge';
import { NOT_AVAILABLE } from '@atlassian/cssxp-utils-entitlement';
import InfoIcon from '@atlaskit/icon/glyph/info';
import {
  FetchEntitlementsGroupByCloudSiteQuery,
  FetchEntitlementsGroupByCloudSiteQueryVariables,
  Maybe,
  EntitlementsGroupByCloudSite,
  EntitlementsGroupByCloudSiteConnection,
} from '@css-xp/graphtypes/src/generated-types-new';
import { isAnnualTermedSubscription } from '@css-xp/ppc-utils';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { CloudSiteLink } from '@css-xp/ppc-common/src/cloud-site-link';
import { AppRows, AppSubRow } from '../../../app-rows';
import { CustomPanel } from '../../../custom-panel';
import { DynamicResultTable } from '../../../dynamic-result-table';
import EntitlementIcon from '../../../entitlement-icon/entitlement-icon';
import { withApollo } from '../../../graphql-provider';
import { fetchEntitlementsGroupByCloudSite } from '../queries/fetch-entitlements-grouped-by-cloud-site';
import './entitlement-grouped-by-cloud-site-table.scss';
import { AddOnLozenge } from '../../../copyable-text';
interface EntitlementByCloudSiteTableProps {
  invoiceGroupId?: string;
  header?: string;
  message?: string;
  setDisablePaymentTermSelection?: Dispatch<SetStateAction<boolean>>;
  isDefaultMethodIsCard?: boolean;
  defaultPaymentMethodId?: string;
  transactionAccountId?: string;
  version?: number;
}

const emptyStateOrderProp = {
  header: '',
  description: 'No other subscriptions will be affected',
  imageUrl: '',
  testId: 'no-results-subscriptions-apps',
};
export const EntitlementGroupByCloudSiteTable: React.FC<EntitlementByCloudSiteTableProps> = ({
  invoiceGroupId,
  header,
  message,
  defaultPaymentMethodId,
  isDefaultMethodIsCard,
  setDisablePaymentTermSelection,
  transactionAccountId,
  version,
}) => {
  const isVersion2 = version === 2;

  const { data, loading, error } = useQuery<
    FetchEntitlementsGroupByCloudSiteQuery,
    FetchEntitlementsGroupByCloudSiteQueryVariables
  >(fetchEntitlementsGroupByCloudSite, {
    variables: {
      invoiceGroupId: invoiceGroupId ? invoiceGroupId : '',
    },
    skip: !invoiceGroupId,
  });

  const entitlementsGroupedByCloudSite = data?.entitlementsGroupByCloudSite;

  React.useEffect(() => {
    //Check for linked entitlements only when the billing profile has either no payment method or payment on terms method.
    if (!defaultPaymentMethodId || !isDefaultMethodIsCard) {
      entitlementsGroupedByCloudSite?.every((entitlementDetails) => {
        const entitlement = entitlementDetails?.entries?.find((entitlement) => {
          const isAnnualTermed = isAnnualTermedSubscription(entitlement as any);
          return !isAnnualTermed;
        });
        if (entitlement) {
          setDisablePaymentTermSelection && setDisablePaymentTermSelection(true);
          return false;
        }
      });
    }
  }, [defaultPaymentMethodId, entitlementsGroupedByCloudSite, setDisablePaymentTermSelection, isDefaultMethodIsCard]);

  const renderEntitlementIcon = (entitlement: Maybe<EntitlementsGroupByCloudSite> | undefined) => {
    return (
      <EntitlementIcon
        logoId={entitlement?.logoId as string}
        productId={entitlement?.productId as string}
        isCloud={true}
        className="avatar medium"
      />
    );
  };

  const renderProductName = (entitlement: Maybe<EntitlementsGroupByCloudSite> | undefined) => {
    return entitlement?.productName ? entitlement?.productName : 'Unnamed product';
  };

  const getRows = (payload: {
    cloudSiteId: string | undefined;
    entries: Maybe<Maybe<EntitlementsGroupByCloudSite>[]> | undefined;
  }) => {
    return payload?.entries?.map((item, index) => {
      const cellData = [
        {
          content: (
            <div className="product-name-and-icon">
              {renderEntitlementIcon(item)}
              <span>{renderProductName(item)}</span>
              {item?.isAddOn && <AddOnLozenge />}
            </div>
          ),
        },
        {
          content: item?.entitlementNumber ? (
            <span className="entitlement-number">{item?.entitlementNumber}</span>
          ) : (
            <span className="entitlement-number" aria-label="Not available" data-testid="not-available">
              {NOT_AVAILABLE}
            </span>
          ),
        },
      ];

      return {
        key: payload?.cloudSiteId ? payload?.cloudSiteId : index.toString(),
        cells: cellData,
      };
    });
  };

  const getHead = () => {
    const cells = [
      {
        content: 'Product',
        shouldTruncate: true,
        width: 30,
      },
      {
        content: 'Entitlement number',
        width: 20,
      },
    ];

    return {
      cells,
    };
  };

  const getLinkedApps = (
    entitlement?: Maybe<EntitlementsGroupByCloudSite> | undefined,
    entries?: Maybe<Maybe<EntitlementsGroupByCloudSite>[]> | undefined
  ) => {
    return entries
      ?.filter((entry) => entry?.isApp && entry?.parentEntitlementId === entitlement?.familyContainerId)
      ?.map((entitlement) => (
        <AppSubRow key={entitlement?.productId} paddingLeft="25px">
          <td>
            <Flex alignItems="center" gap="space.100">
              {renderEntitlementIcon(entitlement)}
              <Flex direction="column">
                <Text>{renderProductName(entitlement)}</Text>
                <Text size="small" color="color.text.subtle">
                  {entitlement?.entitlementNumber}
                </Text>
              </Flex>
            </Flex>
          </td>
        </AppSubRow>
      ));
  };

  const getLinkedAddons = (
    entitlement: Maybe<EntitlementsGroupByCloudSite> | undefined,
    entries?: Maybe<Maybe<EntitlementsGroupByCloudSite>[]> | undefined
  ) => {
    return entries
      ?.filter((entry) => entry?.isAddOn && entry?.parentEntitlementId === entitlement?.entitlementId)
      ?.map((entitlement) => (
        <AppSubRow key={entitlement?.productId} paddingLeft="25px">
          <td>
            <Flex alignItems="center" gap="space.100">
              {renderEntitlementIcon(entitlement)}
              <Flex direction="column">
                <Text>{renderProductName(entitlement)}</Text>
                <Text size="small" color="color.text.subtle">
                  {entitlement?.entitlementNumber}
                </Text>
              </Flex>
            </Flex>
          </td>
        </AppSubRow>
      ));
  };

  const tables =
    entitlementsGroupedByCloudSite && entitlementsGroupedByCloudSite.length > 0 ? (
      entitlementsGroupedByCloudSite.map((entitlementDetails) => {
        const tableTitle = () => [
          () => (
            <div className="cloud-site-name">
              Site name:{' '}
              <span className="cloud-site-name__text">{`${entitlementDetails?.entries?.[0]?.cloudSiteHostname}`}</span>
            </div>
          ),
        ];
        return (
          <div key={entitlementDetails?.cloudSiteId} className="entitlement-grouped-by-cloud-site-table">
            <DynamicResultTable
              boxShadow={false}
              testId="subscriptions-and-apps-table"
              actions={tableTitle()}
              head={getHead()}
              rows={getRows({ cloudSiteId: entitlementDetails?.cloudSiteId, entries: entitlementDetails?.entries })}
              emptyStateProps={emptyStateOrderProp}
              isLoading={loading}
            />
          </div>
        );
      })
    ) : (
      <>
        <div>
          <DynamicResultTable
            boxShadow={false}
            testId="subscriptions-and-apps-table"
            head={getHead()}
            rows={getRows({ cloudSiteId: '', entries: [] })}
            emptyStateProps={emptyStateOrderProp}
            isLoading={loading}
          />
        </div>
      </>
    );

  if (isVersion2) {
    if (loading) {
      return (
        <Box paddingBlockStart="space.200" testId="loading-entitlements">
          <SkeletonLoaderWrapper testId="linked-subscriptions-profiles-loading">
            <Flex>
              <SkeletonLoader width="10%" marginBottom="0" />
              <SkeletonLoader width="90%" marginBottom="0" />
            </Flex>
          </SkeletonLoaderWrapper>
        </Box>
      );
    }

    const header = (
      <Box paddingInlineStart="space.300" testId="linked-subscriptions-header">
        <Flex gap="space.100" alignItems="center">
          {/* Above 500 it will show 500+ in badge */}
          <Badge max={500}>{entitlementsGroupedByCloudSite?.length || '-'}</Badge>
          <Text size="small" weight="semibold">
            linked subscriptions
          </Text>
        </Flex>
      </Box>
    );

    return (
      <Box paddingBlockStart="space.200">
        <CustomPanel testId="entitlements-by-cloud-site" expandedHeader={header} collapsedHeader={header} version={2}>
          {error ? (
            <div className="error-block">
              <ErrorIcon label="Error" size="medium" primaryColor={R300} />
              <span className="error-text">
                It looks like something went wrong. <span className="n-300">Please go back and try again.</span>
              </span>
            </div>
          ) : (
            <Box padding="space.250">
              <Flex direction="column" gap="space.250">
                {entitlementsGroupedByCloudSite?.map(
                  (entitlementDetails?: EntitlementsGroupByCloudSiteConnection | undefined | null) => {
                    return (
                      <Box key={entitlementDetails?.cloudSiteId}>
                        <Box paddingBlockEnd="space.050">
                          <CloudSiteLink
                            siteName={entitlementDetails?.entries?.[0]?.cloudSiteHostname}
                            txaId={transactionAccountId}
                            cloudSiteId={entitlementDetails?.cloudSiteId}
                            shouldRemoveLineClamp={true}
                          />
                        </Box>
                        {entitlementDetails?.entries
                          ?.filter((entitlement) => !entitlement?.isApp && !entitlement?.isAddOn)
                          ?.map((entitlement?: Maybe<EntitlementsGroupByCloudSite>) => {
                            const linkedApps = getLinkedApps(entitlement, entitlementDetails?.entries);
                            const linkedAddons = getLinkedAddons(entitlement, entitlementDetails?.entries);

                            return (
                              <Box key={entitlement?.productId}>
                                <Flex gap="space.100" alignItems="center">
                                  {renderEntitlementIcon(entitlement)}
                                  <Flex direction="column">
                                    <Text>{renderProductName(entitlement)}</Text>
                                    <Text size="small" color="color.text.subtle">
                                      {entitlement?.entitlementNumber}
                                    </Text>
                                  </Flex>
                                </Flex>
                                {linkedApps && linkedApps?.length > 0 && (
                                  <AppRows
                                    appsCount={linkedApps.length}
                                    subRows={linkedApps}
                                    expandableRowLeftPosition="0px"
                                    containerPaddingLeft="0px"
                                  />
                                )}
                                {linkedAddons && linkedAddons?.length > 0 && (
                                  <AppRows
                                    appsCount={linkedAddons.length}
                                    subRows={linkedAddons}
                                    expandableRowLeftPosition="0px"
                                    containerPaddingLeft="0px"
                                    badgeText="installed add on"
                                  />
                                )}
                              </Box>
                            );
                          })}
                      </Box>
                    );
                  }
                )}
              </Flex>
            </Box>
          )}
        </CustomPanel>
      </Box>
    );
  }

  return (
    <div className="entitlements-group-by-cloud-site-table-container">
      <div>
        {' '}
        <p className="n-300">
          {message ? (
            message
          ) : (
            <div className="entitlements-group-by-cloud-site-table-container__info">
              <InfoIcon label="" size="small" primaryColor={'#C377E0'} />
              Keep in mind, billing changes will apply to other subscriptions linked to this product family.
            </div>
          )}
        </p>
      </div>
      <CustomPanel
        testId="entitlements-by-cloud-site"
        expandedHeader={header ? header : `Linked subscriptions that this change will apply to`}
        collapsedHeader={header ? header : `Linked subscriptions that this change will apply to`}
      >
        {error ? (
          <div className="error-block">
            <ErrorIcon label="Error" size="medium" primaryColor={R300} />
            <span className="error-text">
              It looks like something went wrong. <span className="n-300">Please go back and try again.</span>
            </span>
          </div>
        ) : (
          <>{tables}</>
        )}
      </CustomPanel>
    </div>
  );
};

export default withApollo(EntitlementGroupByCloudSiteTable);
